import { errorToast, toast } from "@/v2-ui/toast"
import Badge from "@/v2-backoffice-client/components/badge"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import Manage from "@/v2-backoffice-client/components/manage"
import { useLicenseAddOnGrantMutation } from "@/v2-backoffice-client/license/addOns/license.addOns.grant.mutation"
import {
  describeAddOnGoesWith,
  describeAddOnUnlocks,
  describeLicense,
  isLicenseLive,
  type AddOnLicenseCandidate,
} from "@/v2-backoffice-client/license/addOns/license.addOns.utils"
import { evictLicenseAddOnQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseAddOnsQuery } from "@/v2-backoffice-client/license/addOns/__types__/license.addOns.query"

export type LicenseAddOnManageAvailableItemProps = {
  addOn: LicenseAddOnsQuery["getAddOns"][number]
  /** The customer's licenses the add-on goes with (carryingLicensesOf), live or not. */
  carriers: readonly AddOnLicenseCandidate[]
  customerId: number
  /** The customer, by name, for the sentences. */
  who: string
  statusNameOf(code: number): string | undefined
}

// An add-on the customer does not have yet: what it unlocks, whether it
// would work for them the moment it is added (hover for which license
// carries it, or which one is missing), and Add. One click writes it: an
// add-on has no settings of its own, it goes with a product and whichever
// license the customer holds for that product carries it.
function LicenseAddOnManageAvailableItem(
  props: LicenseAddOnManageAvailableItemProps,
) {
  const { addOn, carriers, customerId, who, statusNameOf } = props
  const liveCarriers = carriers.filter(isLicenseLive)
  const isReady = liveCarriers.length > 0

  const [grant, { loading, error }] = useLicenseAddOnGrantMutation({
    update: evictLicenseAddOnQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Add-on added",
        description: isReady
          ? `${who} has ${addOn.Name}, with the ${liveCarriers.map(describeLicense).join(" and the ")}.`
          : `${who} has ${addOn.Name}. It gives nothing until they have a live license it goes with.`,
      })
    },
  })
  errorToast(error, { title: "Failed to add the add-on" })

  // Why it works at once, or what is in the way: the license this customer
  // holds that carries it, a held license that is not live, or no license
  // it goes with at all.
  function describeReadiness() {
    if (isReady) {
      return `${who} has the ${liveCarriers.map(describeLicense).join(" and the ")}: the add-on works the moment it is added, for the people on that license.`
    }
    const notLive = carriers.filter((license) => !isLicenseLive(license))
    if (notLive.length > 0) {
      return `${who}'s ${notLive.map((license) => `${describeLicense(license)} is ${statusNameOf(license.LicenseStatusCode)?.toLowerCase() ?? "not live"}`).join(" and ")}. You can add the add-on now; it starts working when that license is live again.`
    }
    return `${describeAddOnGoesWith(addOn.ProductNames)}. ${who} has no such license. You can add the add-on now; it starts working once they get one (Grant license, in the Licenses table).`
  }

  return (
    <Manage.Item>
      <Manage.Item.Text
        primary={
          <>
            {addOn.Name}
            {addOn.IsCustom && (
              <Badge
                size="tiny"
                variant="neutral"
                className="ml-1.5 align-middle"
                tooltip="Bespoke: composed for this customer only"
              >
                Bespoke
              </Badge>
            )}
          </>
        }
        secondary={describeAddOnUnlocks(addOn)}
        title={[addOn.Description, describeAddOnUnlocks(addOn)]
          .filter(Boolean)
          .join("\n")}
      />
      <Badge
        size="sm"
        variant={isReady ? "positive" : "warning"}
        tooltip={describeReadiness()}
      >
        {isReady ? "Works at once" : "Needs a license"}
      </Badge>
      <ConfirmButton
        size="xs"
        variant="ghost"
        isSquare
        iconSrc="plus"
        isLoading={loading}
        content={`Add ${addOn.Name} to ${who}`}
        confirmContent="Add?"
        data-feature-code="UI_BackofficeLicense_AddOns_Grant"
        onConfirm={() => {
          grant({ variables: { customerId, addOnId: addOn.Id } })
        }}
      />
    </Manage.Item>
  )
}

export default LicenseAddOnManageAvailableItem
